import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Route, Save, Loader2, Store, Bike, User } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const NUM_FIELDS = [
  { key: 'local_radius_km', label: 'Radio local (km)', hint: 'Tienda, repartidor y comprador deben estar dentro' },
  { key: 'avg_speed_kmh', label: 'Velocidad promedio (km/h)', hint: 'Para la hora estimada local' },
  { key: 'prep_minutes', label: 'Preparación (min)', hint: 'Tiempo de la tienda antes de la recolección' },
  { key: 'driver_freshness_minutes', label: 'Ubicación reciente (min)', hint: 'Repartidor con GPS de hace máximo X min' },
];
const ETA_RULES = [
  { key: 'same', label: 'Misma región' },
  { key: 'adjacent', label: 'Región aledaña (Norte↔Centro, Centro↔Sur)' },
  { key: 'extreme', label: 'Extremo a extremo (Norte↔Sur)' },
];

const Check = ({ ok, icon: Icon, label }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded ${ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-700 text-gray-400'}`}>
    <Icon className="w-3 h-3" />{label}
  </span>
);

export const HeisenbergPanel = ({ algo, onSaved }) => {
  const [params, setParams] = useState(algo.params);
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState([]);

  useEffect(() => { setParams(algo.params); }, [algo.params]);
  useEffect(() => { adminAPI.getHeisenbergExecutions().then(setRuns).catch(() => {}); }, []);

  const setEta = (rule, idx, v) => setParams(p => {
    const pair = [...p.eta_days[rule]]; pair[idx] = v;
    return { ...p, eta_days: { ...p.eta_days, [rule]: pair } };
  });

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateAlgorithm('motor_heisenberg', { params });
      toast({ title: 'Parámetros de Heisenberg guardados' });
      onSaved();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (!params) return null;
  return (
    <Card className="bg-gray-800 border-cyan-700/40 p-6 space-y-6" data-testid="heisenberg-panel">
      <div className="flex items-center gap-3">
        <Route className="w-6 h-6 text-cyan-400" />
        <div>
          <h2 className="text-lg font-semibold text-white">Motor Heisenberg · Parámetros</h2>
          <p className="text-xs text-gray-400">Se usan en la página del producto y al decidir el envío de cada pedido pagado.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {NUM_FIELDS.map(f => (
          <div key={f.key}>
            <label className="text-xs text-gray-400 block mb-1">{f.label}</label>
            <Input type="number" value={params[f.key]} onChange={e => setParams(p => ({ ...p, [f.key]: e.target.value }))}
              className="bg-gray-900 border-gray-600 text-white" data-testid={`heis-${f.key}`} />
            <p className="text-[11px] text-gray-500 mt-1">{f.hint}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-sm text-gray-300 mb-2">Envío nacional · días naturales (mínimo – máximo)</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {ETA_RULES.map(r => (
            <div key={r.key} className="bg-gray-900/60 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-2">{r.label}</p>
              <div className="flex items-center gap-2">
                <Input type="number" value={params.eta_days[r.key][0]} onChange={e => setEta(r.key, 0, e.target.value)} className="bg-gray-800 border-gray-600 text-white h-8" data-testid={`heis-eta-${r.key}-min`} />
                <span className="text-gray-500">–</span>
                <Input type="number" value={params.eta_days[r.key][1]} onChange={e => setEta(r.key, 1, e.target.value)} className="bg-gray-800 border-gray-600 text-white h-8" data-testid={`heis-eta-${r.key}-max`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="heis-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Guardar parámetros
      </Button>
      <div>
        <p className="text-sm text-gray-300 mb-2">Últimas decisiones</p>
        {runs.length === 0 ? <p className="text-xs text-gray-500">Aún no hay pedidos evaluados con la versión 3.</p> : (
          <div className="space-y-1.5" data-testid="heis-executions">
            {runs.map((r, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs bg-gray-900/50 rounded px-3 py-2">
                <span className="font-mono text-gray-400">#{r.order_id?.slice(0, 8)}</span>
                <span className={`font-semibold uppercase ${r.decision === 'local' ? 'text-emerald-400' : 'text-blue-400'}`}>{r.decision}</span>
                {r.checks && <>
                  <Check ok={r.checks.store_located} icon={Store} label="Tienda" />
                  <Check ok={r.checks.driver_in_radius} icon={Bike} label="Repartidor" />
                  <Check ok={r.checks.buyer_in_radius} icon={User} label="Comprador" />
                </>}
                {r.distance_km != null && <span className="text-gray-500">{r.distance_km} km</span>}
                <span className="ml-auto text-gray-600">{new Date(r.timestamp).toLocaleString('es-MX')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
